export default function PrivacyPage() {
  return (
    <div className="min-h-dvh bg-bg">
      {/* Header */}
      <div className="glass sticky top-0 z-20 px-5 py-4 safe-top flex items-center gap-3">
        <a href="/" className="text-white/40 hover:text-white transition-colors">
          ← Back
        </a>
        <div className="flex-1 text-center">
          <h1 className="font-serif text-lg font-bold text-white">Privacy Policy</h1>
        </div>
        <div className="w-12" />
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10 pb-20">

        {/* Title block */}
        <div className="text-center mb-10">
          <img src="/icon.png" alt="Timeline Journal" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <h2 className="font-serif text-3xl font-bold text-white mb-1">Privacy Policy</h2>
          <p className="font-mono text-xs text-white/30 tracking-widest">TIMELINEJOURNAL.APP</p>
          <p className="font-mono text-xs text-white/25 mt-2">
            Effective: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Promise banner */}
        <div className="mb-8 p-5 rounded-2xl border border-green/20 bg-green/5">
          <p className="font-mono text-[10px] text-green tracking-widest uppercase mb-2">Our Core Promise</p>
          <div className="flex flex-col gap-1.5">
            {[
              '❌ We never sell your data',
              '❌ We never run ads in your timeline',
              '❌ We never build advertising profiles about you',
              '✓ Your private entries are only ever visible to you',
              '✓ Your legal vault documents are encrypted and protected',
            ].map(p => (
              <p key={p} className="text-white/70 text-sm">{p}</p>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-8">

          <Section title="1. Information We Collect">
            <SubHeading>Information You Provide</SubHeading>
            <List items={[
              'Account details: name, username, email, and password',
              'Profile information: bio, location, birth year, and photos',
              'Timeline entries: text, photos, videos, voice recordings, and location data',
              'Time capsule content: messages, media, and recipient information',
              'Legal vault documents: wills, medical directives, deeds, insurance policies, and personal letters',
              'Legacy contacts: names, emails, and permission settings',
              'Inform posts: articles, blogs, and community content you publish',
            ]} />
            <SubHeading>Collected Automatically</SubHeading>
            <List items={[
              'Device type, operating system, and browser',
              'Usage data: features used, time spent, actions taken',
              'IP address, access times, and error logs',
              'GPS location — only when you explicitly add it to an entry',
            ]} />
          </Section>

          <Section title="2. How We Use Your Information">
            <Prose>We use your information only to operate and improve the Service:</Prose>
            <List items={[
              'Create and maintain your account and timeline',
              'Store and display your entries, capsules, and vault documents',
              'Deliver time capsules to recipients on your chosen date',
              'Release vault documents to your designated contacts per your trigger settings',
              'Generate your memorial life summary when activated',
              'Send notifications — On This Day reminders, capsule countdowns, and account alerts',
              'Respond to your support requests',
              'Maintain security and comply with legal obligations',
            ]} />
          </Section>

          <Section title="3. Storage and Security">
            <Prose>Your data is stored on Supabase (Amazon Web Services, US). File uploads are stored in access-controlled storage buckets. Key protections include:</Prose>
            <List items={[
              'All data transmitted using HTTPS/TLS encryption',
              'Passwords hashed with bcrypt — never stored in plain text',
              'Row-level security — no user can access another user\'s private data',
              'Legal vault documents stored in private, encrypted storage buckets',
              'Two-factor authentication available for your account',
            ]} />
          </Section>

          <Section title="4. Legal Vault — Special Notice">
            <Prose>The Legal Vault stores sensitive documents including wills and medical directives. Important:</Prose>
            <List items={[
              'Documents are accessible only to you and people you explicitly designate',
              'We release vault documents only when your specified trigger conditions are met',
              'We disclose vault documents to law enforcement only under a valid court order',
              'We notify you of any legal requests whenever legally permitted',
              'Timeline Journal is not a licensed attorney — work with legal counsel for estate planning',
            ]} />
          </Section>

          <Section title="5. Who We Share Data With">
            <Prose>We do not sell, rent, or trade your data. We share only with:</Prose>
            <List items={[
              'Supabase, Inc. — database, authentication, and file storage',
              'Vercel, Inc. — web hosting and delivery',
              'Cloudflare, Inc. — domain and DDoS protection',
              'Law enforcement — only under valid court order or legal process',
              'A successor company — if Timeline Journal is acquired (you will be notified)',
            ]} />
          </Section>

          <Section title="6. Your Privacy Controls">
            <List items={[
              'Every entry can be set to Private, Friends, or Public — changeable anytime',
              'You can set a default privacy level for all new entries',
              'You can edit or delete any entry or your entire account at any time',
              'You control vault document permissions and can modify or revoke them anytime',
              'You can export your data by contacting privacy@timelinejournal.app',
            ]} />
          </Section>

          <Section title="7. Your Legal Rights">
            <Prose>Depending on where you live, you may have the right to access, correct, delete, or export your personal data. To exercise any right, email us at privacy@timelinejournal.app. We respond within 30 days.</Prose>
            <Prose>California residents have additional rights under CCPA. EEA residents have additional rights under GDPR. Contact us for details.</Prose>
          </Section>

          <Section title="8. Law Enforcement Requests">
            <List items={[
              'We require a valid court order or subpoena before disclosing any user data',
              'We do not voluntarily share data with law enforcement',
              'We notify affected users of requests whenever legally permitted',
              'We produce only the minimum data required by the specific legal request',
              'All produced data includes cryptographic verification of integrity',
            ]} />
          </Section>

          <Section title="9. Children\'s Privacy">
            <Prose>Timeline Journal is not directed to children under 13. We do not knowingly collect data from children under 13. If you believe a child has created an account, contact us at privacy@timelinejournal.app and we will delete it promptly.</Prose>
          </Section>

          <Section title="10. Data Retention">
            <Prose>We keep your data as long as your account is active. When you delete your account, we delete your personal data within 30 days, except where required by law. Encrypted backups may retain residual data for up to 90 days.</Prose>
          </Section>

          <Section title="11. Changes to This Policy">
            <Prose>When we make material changes, we will notify you by email and post a notice in the app. Your continued use after changes take effect constitutes acceptance. We encourage you to review this policy periodically.</Prose>
          </Section>

          <Section title="12. Contact Us">
            <Prose>Questions, requests, or concerns about your privacy:</Prose>
            <div className="mt-3 p-4 rounded-xl bg-white/5 border border-white/[0.07]">
              <p className="font-semibold text-white mb-1">Timeline Journal</p>
              <p className="text-white/50 text-sm">
                Email:{' '}
                <a href="mailto:privacy@timelinejournal.app" className="text-blue-light underline">
                  privacy@timelinejournal.app
                </a>
              </p>
              <p className="text-white/50 text-sm">
                Website:{' '}
                <a href="https://timelinejournal.app" className="text-blue-light underline">
                  timelinejournal.app
                </a>
              </p>
            </div>
          </Section>

        </div>

        {/* Footer note */}
        <div className="mt-10 pt-6 border-t border-white/[0.07]">
          <p className="text-white/20 text-xs text-center leading-relaxed font-mono">
            This policy was last updated{' '}
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
            {' '}Timeline Journal recommends consulting a licensed attorney regarding legal vault and estate planning compliance.
          </p>
        </div>

      </div>
    </div>
  )
}

// ── Small helper components ──────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h3 className="font-serif text-xl font-bold text-white mb-4 pb-3 border-b border-white/[0.07]">
        {title}
      </h3>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-mono text-[10px] text-white/40 tracking-widest uppercase mt-2">
      {children}
    </p>
  )
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-white/60 text-sm leading-relaxed">{children}</p>
  )
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map(item => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-white/60">
          <span className="text-blue-light mt-0.5 flex-shrink-0">›</span>
          {item}
        </li>
      ))}
    </ul>
  )
}
